import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';
import type { Props as LoginModalProps } from './LoginModal';
import type { PartialConfigurableText } from './types';

type ForkLoader = () => Promise<{ LoginModal: ComponentType<LoginModalProps> }>;

export type ModalVariantEntry = {
  configuredText?: PartialConfigurableText;
  forkComponent?: LazyExoticComponent<ComponentType<LoginModalProps>>;
  forkLoader?: ForkLoader;
};

// Each fork is its own dynamic-import chunk. LoginModalManager calls
// forkLoader() on mount once the real-test bucket is known, so the
// chunk is loaded well before any CTA click. React.lazy resolves
// from the browser's module cache when render-time arrives — the
// Suspense fallback (null) only kicks in if the chunk somehow
// hasn't arrived yet (very slow network + fast click), in which
// case the modal opens a beat later rather than flashing control
// before the variant.
const variantB4Loader: ForkLoader = () =>
  import('./autotest/variant_b4/LoginModal');
const variantF1Loader: ForkLoader = () =>
  import('./autotest/variant_f1/LoginModal');
const variantF2Loader: ForkLoader = () =>
  import('./autotest/variant_f2/LoginModal');
const variantF4Loader: ForkLoader = () =>
  import('./autotest/variant_f4/LoginModal');
const variantF5Loader: ForkLoader = () =>
  import('./autotest/variant_f5/LoginModal');
const variantH1Loader: ForkLoader = () =>
  import('./autotest/variant_h1/LoginModal');
const variantH3Loader: ForkLoader = () =>
  import('./autotest/variant_h3/LoginModal');

const VariantB4 = lazy(() =>
  variantB4Loader().then((m) => ({ default: m.LoginModal })),
);
const VariantF1 = lazy(() =>
  variantF1Loader().then((m) => ({ default: m.LoginModal })),
);
const VariantF2 = lazy(() =>
  variantF2Loader().then((m) => ({ default: m.LoginModal })),
);
const VariantF4 = lazy(() =>
  variantF4Loader().then((m) => ({ default: m.LoginModal })),
);
const VariantF5 = lazy(() =>
  variantF5Loader().then((m) => ({ default: m.LoginModal })),
);
const VariantH1 = lazy(() =>
  variantH1Loader().then((m) => ({ default: m.LoginModal })),
);
const VariantH3 = lazy(() =>
  variantH3Loader().then((m) => ({ default: m.LoginModal })),
);

export const MODAL_VARIANT_REGISTRY: Record<string, ModalVariantEntry> = {
  control: {},
  variant_b1: {
    configuredText: {
      buttonsPage: {
        header: "To read '{paperTitle}' please sign up",
      },
    },
  },
  variant_b2: {
    configuredText: {
      buttonsPage: {
        header: "Sign up to read '{paperTitle}'",
        subheader: 'by the authors',
      },
    },
  },
  variant_b3: {
    configuredText: {
      buttonsPage: {
        header: "Free academic library — including '{paperTitle}'",
        subheader: 'Sign up for instant access',
      },
    },
  },
  variant_b4: { forkComponent: VariantB4, forkLoader: variantB4Loader },
  variant_b5: {
    configuredText: {
      emailRegistrationPage: {
        subheader: "You're 30 seconds from reading '{paperTitle}'",
      },
    },
  },
  variant_b6: {
    configuredText: {
      buttonsPage: {
        header: "To read '{paperTitle}' please sign up",
      },
      emailEntryPage: {
        subheader: "To read '{paperTitle}'",
      },
      emailRegistrationPage: {
        subheader: "You're 30 seconds from reading '{paperTitle}'",
      },
    },
  },
  variant_c1: {
    configuredText: {
      buttonsPage: {
        header: 'Step 1 of 1: sign up',
      },
    },
  },
  variant_c2: {
    configuredText: {
      buttonsPage: {
        subheader: '5 seconds with Google — really',
      },
    },
  },
  variant_c3: {
    configuredText: {
      emailRegistrationPage: {
        subheader: 'Last step — almost there',
      },
    },
  },
  variant_d1: {
    configuredText: {
      buttonsPage: {
        googleButton: 'Download with Google',
        appleButton: 'Download with Apple',
        emailButton: 'Download with Email',
      },
    },
  },
  variant_d2: {
    configuredText: {
      buttonsPage: {
        googleButton: 'Sign up & continue with Google',
        appleButton: 'Sign up & continue with Apple',
        emailButton: 'Sign up & continue with Email',
      },
    },
  },
  variant_d3: {
    configuredText: {
      emailRegistrationPage: {
        submit: 'Sign Up & Download',
      },
    },
  },
  variant_f1: { forkComponent: VariantF1, forkLoader: variantF1Loader },
  variant_f2: { forkComponent: VariantF2, forkLoader: variantF2Loader },
  variant_f4: { forkComponent: VariantF4, forkLoader: variantF4Loader },
  variant_f5: { forkComponent: VariantF5, forkLoader: variantF5Loader },
  variant_h1: {
    forkComponent: VariantH1,
    forkLoader: variantH1Loader,
    configuredText: {
      buttonsPage: {
        header: "To read '{paperTitle}' please sign up",
        subheader: '5 seconds with Google — really',
      },
    },
  },
  variant_h2: {
    configuredText: {
      buttonsPage: {
        header: "To read '{paperTitle}' please sign up",
        subheader: '5 seconds with Google — really',
      },
    },
  },
  variant_h3: {
    forkComponent: VariantH3,
    forkLoader: variantH3Loader,
    configuredText: {
      buttonsPage: {
        subheader: '5 seconds with Google — really',
      },
    },
  },
};

export function lookupModalVariant(bucket: string): ModalVariantEntry {
  return MODAL_VARIANT_REGISTRY[bucket] ?? {};
}

// Deep-merges a per-page PartialConfigurableText override on top of a
// caller-supplied PartialConfigurableText. The variant's keys win where
// they overlap; the caller's keys remain for any page or field the
// variant doesn't touch. Phase 1 variants all have empty overrides so
// this is a no-op — but Phase 2 variants depend on it.
export function mergeConfiguredText(
  caller: PartialConfigurableText | undefined,
  variant: PartialConfigurableText | undefined,
): PartialConfigurableText | undefined {
  if (!variant) return caller;
  if (!caller) return variant;
  return {
    buttonsPage: { ...caller.buttonsPage, ...variant.buttonsPage },
    emailEntryPage: { ...caller.emailEntryPage, ...variant.emailEntryPage },
    emailRegistrationPage: {
      ...caller.emailRegistrationPage,
      ...variant.emailRegistrationPage,
    },
    emailLoginPage: { ...caller.emailLoginPage, ...variant.emailLoginPage },
  };
}

// Work-context substitution for autotest variant copy.
//
// Variants that want to acknowledge the specific paper a visitor is
// trying to access (Cluster B — paper anchoring) can put a `{paperTitle}`
// token in any configuredText string. At modal-open time, the token is
// replaced with `workTitle` from the caller's event detail. Callers
// without a work context (non-LOSWP callers, or LOSWP callers that
// don't pass workTitle) fall back to a generic phrase so the copy
// still reads correctly.
//
// Token format: `{paperTitle}`. Single brace-delimited identifier;
// unlikely to collide with intentional literal copy.
export const PAPER_TITLE_FALLBACK = 'this paper';

export type WorkContext = {
  workTitle?: string;
};

export function substituteWorkContext(
  text: PartialConfigurableText | undefined,
  ctx: WorkContext,
): PartialConfigurableText | undefined {
  if (!text) return text;
  const title = ctx.workTitle?.trim() || PAPER_TITLE_FALLBACK;
  const sub = (s: string | undefined): string | undefined =>
    s?.replace(/\{paperTitle\}/g, title);
  return {
    buttonsPage: text.buttonsPage && {
      ...text.buttonsPage,
      header: sub(text.buttonsPage.header),
      subheader: sub(text.buttonsPage.subheader),
      googleButton: sub(text.buttonsPage.googleButton),
      appleButton: sub(text.buttonsPage.appleButton),
      facebookButton: sub(text.buttonsPage.facebookButton),
      emailButton: sub(text.buttonsPage.emailButton),
    },
    emailEntryPage: text.emailEntryPage && {
      ...text.emailEntryPage,
      header: sub(text.emailEntryPage.header),
      subheader: sub(text.emailEntryPage.subheader),
    },
    emailRegistrationPage: text.emailRegistrationPage && {
      ...text.emailRegistrationPage,
      header: sub(text.emailRegistrationPage.header),
      subheader: sub(text.emailRegistrationPage.subheader),
      submit: sub(text.emailRegistrationPage.submit),
    },
    emailLoginPage: text.emailLoginPage && {
      ...text.emailLoginPage,
      subheader: sub(text.emailLoginPage.subheader),
    },
  };
}
